import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calculator, ArrowLeft, TrendUp, TrendDown, ArrowRight, Coins, ShieldCheck, ShareNetwork } from "@phosphor-icons/react";
import ShareCardModal from "../components/ShareCardModal";
import ShareTradeSetupCard from "../components/ShareTradeSetupCard";

/**
 * Standalone Risk : Reward calculator.
 *
 * Inputs:  Entry · Stop Loss · Target · Capital · Risk % per trade
 * Outputs: Risk/share · Reward/share · R:R · Qty · Investment · Risk ₹ · Reward ₹
 */
const fmtINR = (n) =>
  typeof n === "number" && Number.isFinite(n)
    ? n.toLocaleString("en-IN", { maximumFractionDigits: 0 })
    : "—";

function StatBox({ label, value, suffix, tone, hint, testid }) {
  const tone_cls =
    tone === "pos" ? "text-pos" :
    tone === "neg" ? "text-neg" :
    tone === "info" ? "text-info" : "text-primary-fg";
  return (
    <div className="border border-subtle rounded-sm p-3 bg-surface/60" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1">
        {label}
      </div>
      <div className={"font-mono tabular-nums text-2xl " + tone_cls}>
        {value}{suffix && <span className="text-sm ml-0.5">{suffix}</span>}
      </div>
      {hint && <div className="text-[10px] text-muted-fg mt-1">{hint}</div>}
    </div>
  );
}

export default function RiskCalculator() {
  // Pre-filled with a working example so users immediately see how the
  // calculator behaves (Qty / Investment / R:R) — they can overwrite with
  // their own numbers from there.
  const [entry, setEntry] = useState("250");
  const [stop, setStop] = useState("240");
  const [target, setTarget] = useState("280");
  const [capital, setCapital] = useState("100000");
  const [riskPct, setRiskPct] = useState("1");
  const [shareOpen, setShareOpen] = useState(false);

  const e = parseFloat(entry);
  const s = parseFloat(stop);
  const t = parseFloat(target);
  const cap = parseFloat(capital);
  const rp = parseFloat(riskPct);
  const isLong = e > s;

  const result = useMemo(() => {
    if (!isFinite(e) || !isFinite(s) || e === s) return null;
    const riskPerShare = Math.abs(e - s);
    const rewardPerShare = isFinite(t) ? Math.abs(t - e) : null;
    const rr = rewardPerShare && riskPerShare > 0 ? rewardPerShare / riskPerShare : null;
    const riskBudget = isFinite(cap) && isFinite(rp) ? (cap * rp) / 100 : null;
    const qty = riskBudget && riskPerShare > 0 ? Math.floor(riskBudget / riskPerShare) : null;
    const investment = qty ? qty * e : null;
    const totalRisk = qty ? qty * riskPerShare : null;
    const totalReward = qty && rewardPerShare ? qty * rewardPerShare : null;
    const slPct = e > 0 ? (riskPerShare / e) * 100 : null;
    const tgtPct = e > 0 && rewardPerShare ? (rewardPerShare / e) * 100 : null;
    return {
      riskPerShare, rewardPerShare, rr, riskBudget,
      qty, investment, totalRisk, totalReward, slPct, tgtPct,
    };
  }, [e, s, t, cap, rp]);

  return (
    <div className="min-h-screen bg-app text-primary-fg">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-10">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-fg hover:text-primary-fg transition mb-6"
        >
          <ArrowLeft size={11} weight="bold" />
          Back to Dashboard
        </Link>

        <div className="flex items-center gap-2 mb-1">
          <Calculator size={20} weight="fill" className="text-pos" />
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight">Risk : Reward Calculator</h1>
        </div>
        <p className="text-sm text-secondary-fg leading-relaxed mb-7 max-w-xl">
          Apne trade ka risk-reward ratio, position size, aur potential P&L
          calculate karo — Entry / Stop Loss / Target enter karke. Capital +
          risk % ke base pe qty auto-compute hoti hai.
        </p>

        {/* Trade inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">
              Entry Price (₹)
            </label>
            <input
              type="number" inputMode="decimal" min="0" step="0.05"
              value={entry} onChange={(ev) => setEntry(ev.target.value)}
              placeholder="e.g. 250.50"
              data-testid="rr-entry"
              className="w-full bg-surface border border-subtle rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-pos transition tabular-nums"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-neg mb-1.5">
              Stop Loss (₹)
            </label>
            <input
              type="number" inputMode="decimal" min="0" step="0.05"
              value={stop} onChange={(ev) => setStop(ev.target.value)}
              placeholder="e.g. 240.00"
              data-testid="rr-stop"
              className="w-full bg-surface border border-subtle rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-neg transition tabular-nums"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-pos mb-1.5">
              Target (₹)
            </label>
            <input
              type="number" inputMode="decimal" min="0" step="0.05"
              value={target} onChange={(ev) => setTarget(ev.target.value)}
              placeholder="e.g. 280.00"
              data-testid="rr-target"
              className="w-full bg-surface border border-subtle rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-pos transition tabular-nums"
            />
          </div>
        </div>

        {/* Capital + risk */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">
              Trading Capital (₹)
            </label>
            <input
              type="number" inputMode="decimal" min="0" step="1000"
              value={capital} onChange={(ev) => setCapital(ev.target.value)}
              data-testid="rr-capital"
              className="w-full bg-surface border border-subtle rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-pos transition tabular-nums"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">
              Risk per Trade (% of capital)
            </label>
            <input
              type="number" inputMode="decimal" min="0.1" max="10" step="0.25"
              value={riskPct} onChange={(ev) => setRiskPct(ev.target.value)}
              data-testid="rr-risk-pct"
              className="w-full bg-surface border border-subtle rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-pos transition tabular-nums"
            />
            <div className="text-[10px] text-muted-fg mt-1">
              Most pros stick to 0.5% – 2% per trade.
            </div>
          </div>
        </div>

        {/* Results */}
        {result ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
            data-testid="rr-results"
          >
            {/* Direction badge */}
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest flex-wrap">
              <span className={
                "px-2 py-1 border rounded-sm flex items-center gap-1 " +
                (isLong ? "border-pos/60 text-pos bg-pos-soft/30" : "border-neg/60 text-neg bg-neg-soft/30")
              }>
                {isLong ? <TrendUp size={11} weight="fill" /> : <TrendDown size={11} weight="fill" />}
                {isLong ? "Long" : "Short"} Setup
              </span>
              {result.rr != null && (
                <span className={
                  "px-2 py-1 border rounded-sm " +
                  (result.rr >= 2
                    ? "border-pos text-pos bg-pos-soft/40"
                    : result.rr >= 1.5
                    ? "border-mod text-mod bg-mod-soft/30"
                    : "border-neg text-neg bg-neg-soft/30")
                }>
                  R : R = 1 : {result.rr.toFixed(2)}
                  {result.rr >= 2 ? " · Strong" : result.rr >= 1.5 ? " · Decent" : " · Weak"}
                </span>
              )}
              {result.qty != null && (
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  data-testid="rr-share-btn"
                  className="ml-auto inline-flex items-center gap-1 px-2 py-1 border border-pos/40 text-pos rounded-sm hover:bg-pos-soft/20 transition"
                  aria-label="Share trade setup"
                >
                  <ShareNetwork size={11} weight="bold" />
                  Share
                </button>
              )}
            </div>

            {/* Per-share stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <StatBox
                label="Risk / Share" value={`₹${fmtINR(result.riskPerShare)}`}
                tone="neg" testid="rr-risk-share"
                hint={result.slPct != null ? `${result.slPct.toFixed(2)}% below entry` : null}
              />
              <StatBox
                label="Reward / Share"
                value={result.rewardPerShare != null ? `₹${fmtINR(result.rewardPerShare)}` : "—"}
                tone="pos" testid="rr-reward-share"
                hint={result.tgtPct != null ? `${result.tgtPct.toFixed(2)}% above entry` : null}
              />
              <StatBox
                label="R : R Ratio"
                value={result.rr != null ? `1 : ${result.rr.toFixed(2)}` : "—"}
                tone={result.rr >= 2 ? "pos" : result.rr >= 1.5 ? "info" : "neg"}
                testid="rr-ratio"
                hint={
                  result.rr >= 2 ? "≥ 1:2 is optimal" :
                  result.rr >= 1.5 ? "Acceptable — keep tight" :
                  result.rr ? "Avoid — reward too small" : null
                }
              />
            </div>

            {/* Position sizing */}
            {result.qty != null && (
              <div className="border border-pos/30 bg-pos-soft/10 rounded-sm p-4 space-y-3" data-testid="rr-position-block">
                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-pos">
                  <Coins size={11} weight="fill" />
                  Position Sizing
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <StatBox
                    label="Quantity" value={fmtINR(result.qty)} suffix=" sh"
                    tone="info" testid="rr-qty"
                  />
                  <StatBox
                    label="Investment" value={`₹${fmtINR(result.investment)}`}
                    testid="rr-investment"
                  />
                  <StatBox
                    label="Risk (Total)" value={`₹${fmtINR(result.totalRisk)}`}
                    tone="neg" testid="rr-total-risk"
                  />
                  <StatBox
                    label="Reward (Total)"
                    value={result.totalReward != null ? `₹${fmtINR(result.totalReward)}` : "—"}
                    tone="pos" testid="rr-total-reward"
                  />
                </div>
                <div className="text-[11px] text-muted-fg leading-relaxed">
                  At <span className="text-secondary-fg">₹{fmtINR(cap)}</span> capital with
                  <span className="text-secondary-fg"> {rp}%</span> risk per trade, you can buy
                  <span className="text-info"> {fmtINR(result.qty)} shares</span> for a max loss of
                  <span className="text-neg"> ₹{fmtINR(result.totalRisk)}</span>.
                  {result.totalReward && (
                    <> If target hits, you make <span className="text-pos">₹{fmtINR(result.totalReward)}</span>.</>
                  )}
                </div>
              </div>
            )}

            {/* Quick guidance */}
            <div className="border-l-2 border-info/50 bg-info-soft/10 p-3 text-[11px] text-secondary-fg leading-relaxed flex gap-2 items-start">
              <ShieldCheck size={14} weight="fill" className="text-info shrink-0 mt-0.5" />
              <div>
                <strong className="text-info">Rule of thumb:</strong> Aim for 1:2 R:R or better.
                If R:R below 1:1.5 hai, trade reconsider karo — small win rate slip karne pe
                badi P&L hit ho sakti hai.
              </div>
            </div>
          </motion.div>
        ) : (
          <div className="border border-subtle rounded-sm p-6 text-center text-sm text-muted-fg">
            Enter Entry &amp; Stop Loss (and Target) to see your R:R ratio and position size.
          </div>
        )}

        <div className="mt-8 text-[10px] text-muted-fg/70 italic flex items-center gap-1">
          <ArrowRight size={10} />
          Open any stock from the scanner → the position sizer auto-fills the same
          calculation using the AI-generated Entry / SL / Target.
        </div>

        {result && result.qty != null && (
          <ShareCardModal
            open={shareOpen}
            onClose={() => setShareOpen(false)}
            title="Share your trade setup"
            shareText={`📊 My trade plan: ${isLong ? "Long" : "Short"} @ ₹${e} · SL ₹${s} · Target ₹${t} · R:R 1:${result.rr?.toFixed(2)} — calculated on Breakout Scanner.`}
            shareUrl="https://breakoutscanner.co.in/risk-calculator"
            filename="trade-setup.png"
          >
            <ShareTradeSetupCard
              entry={e}
              stop={s}
              target={t}
              qty={result.qty}
              capital={cap}
              riskPct={rp}
              rr={result.rr}
              totalRisk={result.totalRisk}
              totalReward={result.totalReward}
              riskPerShare={result.riskPerShare}
              rewardPerShare={result.rewardPerShare}
              isLong={isLong}
            />
          </ShareCardModal>
        )}
      </div>
    </div>
  );
}
